import { useRef, useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import type { ApiMessage } from "../../types/api";
import type { CustomerGroup, CustomerGroupsResponse } from "../../types/customerGroups";

export function CustomerGroupsViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroupsResponse>({
    current_page: 1, data: [], last_page: 1, per_page: 50, total: 0,
  });

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const lastListFetchRef = useRef<{ search: string | null; page: number }>({ search: null, page: 1 });

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getCustomerGroups = async function (search: string | null, page: number) {
    lastListFetchRef.current = { search, page };
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<CustomerGroupsResponse, typeof payload>(`customerGroups`, payload);
    setLoading(false);
    if (callResult.success) {
      setCustomerGroups(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch customer groups");
    }
  };

  const deleteCustomerGroup = async function (id: number) {
    const callResult = await makePostApiCall<ApiMessage>(`deleteCustomerGroup/${id}`);

    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      showSuccess(callResult.data?.message || "Customer group deleted");
      const { search, page } = lastListFetchRef.current;
      await getCustomerGroups(search, page);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const msg = callResult.data?.message || "Could not delete customer group";
      showErrorModal("Could not delete customer group", msg);
    }
  };

  const openDeleteCustomerGroupModal = function (group: CustomerGroup) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteCustomerGroup(group.id)}
          message={`Delete customer group "${group.name}"? Customers assigned to this group will be unassigned (the customer records will not be deleted), and all price list entries for this group will be permanently deleted. This cannot be undone.`}
          setOpen={setEmptyModelOpen}
          title="Delete customer group"
        />
      ),
      open: true,
    });
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    getCustomerGroups,
    openDeleteCustomerGroupModal,
    loading,
    customerGroups,
    setSlideOver,
    slideOver,
  };
}
